import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdminPipelineQueue } from '@libs/types';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useTrans } from '@libs/i18n';
import {
  ArrowSquareOutIcon,
  BuildingsIcon,
  PulseIcon,
  SpinnerGapIcon,
  UsersIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import { useAdminDashboardSummary, useAdminIncidents,useAdminInFlight } from '../api/admin-api';
import { formatRelative } from '../components/adminFormat';
import { AdminPanel, AdminPanelHead } from '../components/AdminPanel';
import { AdminStatusChip } from '../components/AdminStatusChip';
import { adminButtonSx, adminColors } from '../components/adminTheme';
import { StatCard } from '../components/StatCard';

const QueueMeter: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { data: summary } = useAdminDashboardSummary();
  const { data: inFlight, isLoading } = useAdminInFlight();
  const navigate = useNavigate();

  const q: AdminPipelineQueue = summary?.queue ?? {
    processing: 0,
    queued: 0,
    completedToday: 0,
    failed: 0,
  };
  const rows = [
    { key: 'processing', label: t('app.admin.dashboard.queue.processing'), value: q.processing, color: c.azure },
    { key: 'queued', label: t('app.admin.dashboard.queue.queued'), value: q.queued, color: c.warn },
    { key: 'completedToday', label: t('app.admin.dashboard.queue.completedToday'), value: q.completedToday, color: c.green },
    { key: 'failed', label: t('app.admin.dashboard.queue.failed'), value: q.failed, color: c.error },
  ];
  const max = Math.max(1, ...rows.map((r) => r.value));

  return (
    <AdminPanel>
      <AdminPanelHead
        icon={<PulseIcon size={18} />}
        iconColor={c.azure}
        title={t('app.admin.dashboard.queue.title')}
        sub={t('app.admin.dashboard.queue.subtitle')}
      />
      <Box sx={{ px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 1.625 }}>
        {rows.map((r) => (
          <Box key={r.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.625 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: r.color }} />
              <Typography sx={{ flex: 1, fontSize: 12.5, color: c.ink }}>{r.label}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: r.color }}>{r.value}</Typography>
            </Box>
            <Box sx={{ height: 6, backgroundColor: theme.palette.grey[300], borderRadius: 999, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.max(6, (r.value / max) * 100)}%`,
                  backgroundColor: r.color,
                  borderRadius: 999,
                  transition: 'width .7s',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ borderTop: `1px solid ${c.border}`, px: 2.25, py: 1.5, flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: c.faint, letterSpacing: '0.05em', mb: 1.25 }}>
          {t('app.admin.dashboard.queue.inFlightRuns')}
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : !inFlight?.length ? (
          <Typography sx={{ fontSize: 12, color: c.faint }}>{t('app.admin.dashboard.queue.empty')}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.375 }}>
            {inFlight.map((run) => (
              <Box
                key={run.id}
                onClick={() => navigate(routes.adminTenantDetail(run.tenantId))}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: 12.5, color: c.ink, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {run.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: c.faint }}>
                    {t('app.admin.dashboard.queue.documents', { n: run.documentCount })} · {formatRelative(run.updatedAt)}
                  </Typography>
                </Box>
                <AdminStatusChip status={run.status} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </AdminPanel>
  );
});

const Incidents: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const navigate = useNavigate();
  const { data, isLoading } = useAdminIncidents({ page: 1, limit: 10 });
  const incidents = data?.rows ?? [];
  const total = data?.meta.totalCount ?? 0;

  return (
    <AdminPanel>
      <AdminPanelHead
        icon={<WarningIcon size={18} />}
        iconColor={c.error}
        title={t('app.admin.dashboard.incidents.title')}
        sub={t('app.admin.dashboard.incidents.subtitle')}
        right={
          <Box
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: c.error,
              backgroundColor: '#FFF1F0',
              border: '1px solid #FFCCC7',
              borderRadius: 999,
              px: 1.125,
              py: 0.25,
            }}
          >
            {t('app.admin.dashboard.incidents.open', { n: total })}
          </Box>
        }
      />
      <Box sx={{ px: 2, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.375, flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : !incidents.length ? (
          <Typography sx={{ fontSize: 12.5, color: c.faint, py: 1 }}>
            {t('app.admin.dashboard.incidents.empty')}
          </Typography>
        ) : (
          incidents.map((x) => (
            <Box key={x.id} sx={{ border: `1px solid ${c.border}`, borderRadius: 3, p: 1.625 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.875 }}>
                <AdminStatusChip status={x.status} />
                <Typography
                  sx={{ flex: 1, fontSize: 12.5, fontWeight: 500, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {x.title}
                </Typography>
                <Typography sx={{ fontSize: 11, color: c.faint }}>{formatRelative(x.updatedAt)}</Typography>
              </Box>
              {x.failureReason && (
                <Typography sx={{ fontSize: 11.5, color: c.sub, lineHeight: 1.5, mb: 1.25 }}>
                  {x.failureReason}
                </Typography>
              )}
              <Button
                onClick={() => navigate(routes.adminTenantDetail(x.tenantId))}
                startIcon={<ArrowSquareOutIcon size={14} />}
                sx={{ ...adminButtonSx(theme).ghost, fontSize: 12, px: 1.25, py: 0.75, borderRadius: 2, border: `1px solid ${c.border}` }}
              >
                {t('app.admin.dashboard.incidents.openOrg')}
              </Button>
            </Box>
          ))
        )}
      </Box>
    </AdminPanel>
  );
});

export const AdminDashboardPage: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { data: summary } = useAdminDashboardSummary();

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 2.75 }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 23, fontWeight: 700, color: c.ink, mb: 0.375 }}>
          {t('app.admin.dashboard.title')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: c.sub }}>{t('app.admin.dashboard.subtitle')}</Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.75, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' } }}>
        <StatCard
          icon={<BuildingsIcon size={16} />}
          iconColor={c.orange}
          label={t('app.admin.dashboard.stats.activeOrgs')}
          value={summary?.activeTenants ?? '—'}
          sub={t('app.admin.dashboard.stats.activeOrgsSub', { n: summary?.totalTenants ?? 0 })}
        />
        <StatCard
          icon={<UsersIcon size={16} />}
          iconColor={c.azure}
          label={t('app.admin.dashboard.stats.users')}
          value={summary?.totalUsers ?? '—'}
          sub={t('app.admin.dashboard.stats.usersSub')}
        />
        <StatCard
          icon={<SpinnerGapIcon size={16} />}
          iconColor={c.azure}
          label={t('app.admin.dashboard.stats.inFlight')}
          value={summary?.queue.processing ?? '—'}
          sub={t('app.admin.dashboard.stats.inFlightSub', { n: summary?.queue.queued ?? 0 })}
        />
        <StatCard
          icon={<WarningIcon size={16} />}
          iconColor={c.error}
          label={t('app.admin.dashboard.stats.failed')}
          value={summary?.failed24h ?? '—'}
          sub={t('app.admin.dashboard.stats.failedSub')}
          accentBorder="#FFCCC7"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.5fr) minmax(0,1fr)' },
          alignItems: 'start',
        }}
      >
        <QueueMeter />
        <Incidents />
      </Box>
    </Box>
  );
});
